import { useAreParamsIdsValid } from "@/utils/hooksUtils";
import { ATTACHMENT_VIEWER_ID } from "@/utils/utils";
import { Result, Spin } from "antd";
import { AxiosPromise } from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { useOpenAttachment } from "../../../../common/api/queries";
import { EntityIdObject, TwoLevelEntityIdObject } from "../../../../common/types";
import { agentApi } from "../../../../modules/agent/api";
import { bugReportApi } from "../../../../modules/bugreport/api";
import { calculatorsApi } from "../../../../modules/calculator/calcs/api";
import { calcDraftsApi } from "../../../../modules/calculator/drafts/api";
import { calcSettingsApi } from "../../../../modules/calculator/settings/api";
import { clientApi } from "../../../../modules/client/api";
import { commissionBatchApi } from "../../../../modules/commissions/batch/api";
import { commissionsSettingsLevelApi } from "../../../../modules/commissions/level/api";
import { commissionOutputApi } from "../../../../modules/commissions/outputs/api";
import { commissionsSettingsApi } from "../../../../modules/commissions/settings/api";
import { contractApi } from "../../../../modules/contract/api";
import { dashboardApi } from "../../../../modules/dashboard/api";
import { documentsSignApi } from "../../../../modules/docsignature/api";
import { documentsApi } from "../../../../modules/documents/api";
import { financialMediationApi } from "../../../../modules/financialmediation/api";
import { FinancialMediationVersion } from "../../../../modules/financialmediation/enums";
import { NotFound } from "../../../pages/NotFound";
import { ATTACHMENT_BOUNDARY_TYPE } from "./enums";

type AttachmentParams = EntityIdObject & TwoLevelEntityIdObject & { searchParams: URLSearchParams };

type AttachmentDownload = {
  download: (params: AttachmentParams) => AxiosPromise<Blob>;
  forceSave?: boolean;
};

const ATTACHMENT_DOWNLOADS: Record<ATTACHMENT_BOUNDARY_TYPE, AttachmentDownload> = {
  [ATTACHMENT_BOUNDARY_TYPE.AGENT]: { download: agentApi.downloadAgentAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.AGENT_IMPORT_RESULT]: { download: agentApi.downloadAgentsImportResult, forceSave: true },
  [ATTACHMENT_BOUNDARY_TYPE.BUG_REPORT]: { download: bugReportApi.downloadBugReportAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.CALCULATOR_DRAFT]: { download: calcDraftsApi.downloadCalcDraftAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.CALCULATOR_SETTING]: { download: calcSettingsApi.downloadAttachmentConf },
  [ATTACHMENT_BOUNDARY_TYPE.CALCULATOR_RESULT]: { download: calculatorsApi.downloadCalcResultAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.CLIENT]: { download: clientApi.downloadClientAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.CLIENT_IMPORT_RESULT]: { download: clientApi.downloadClientsImportResult, forceSave: true },
  [ATTACHMENT_BOUNDARY_TYPE.COMMISSIONS_BATCH_INPUT]: {
    download: commissionBatchApi.downloadCommissionsBatchInput,
  },
  [ATTACHMENT_BOUNDARY_TYPE.COMMISSIONS_BATCH_OUTPUT]: {
    download: commissionBatchApi.downloadCommissionsBatchOutput,
  },
  [ATTACHMENT_BOUNDARY_TYPE.COMMISSIONS_LEVEL]: {
    download: commissionsSettingsLevelApi.downloadCommissionsLevelAttachment,
  },
  [ATTACHMENT_BOUNDARY_TYPE.COMMISSIONS_SETTINGS]: {
    download: commissionsSettingsApi.downloadCommissionsSettingsAttachment,
  },
  [ATTACHMENT_BOUNDARY_TYPE.COMMISSIONS_OUTPUT]: { download: commissionOutputApi.downloadAgentCommissionsOutput },
  [ATTACHMENT_BOUNDARY_TYPE.CONTRACT]: { download: contractApi.downloadContractAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.CONTRACT_IMPORT_RESULT]: {
    download: contractApi.downloadContractsImportResult,
    forceSave: true,
  },
  [ATTACHMENT_BOUNDARY_TYPE.DASHBOARD_NOTICE]: { download: dashboardApi.downloadDashboardNoticeAttachment },
  [ATTACHMENT_BOUNDARY_TYPE.DOCUMENT_NODE]: { download: documentsApi.downloadDocumentNode },
  [ATTACHMENT_BOUNDARY_TYPE.FINANCIAL_MEDIATION]: {
    download: ({ id, searchParams }) =>
      financialMediationApi.generateFinancialMediation({
        id,
        object: { version: searchParams.get("version") as FinancialMediationVersion },
      }),
  },
  [ATTACHMENT_BOUNDARY_TYPE.CLIENT_DOCUMENT_SIGN_ORIGINAL]: { download: documentsSignApi.downloadOriginalDocument },
  [ATTACHMENT_BOUNDARY_TYPE.CLIENT_DOCUMENT_SIGN_SIGNED]: { download: documentsSignApi.downloadSignedDocument },
};

type Props = {
  type: ATTACHMENT_BOUNDARY_TYPE;
};

export const AttachmentBoundary = ({ type }: Props) => {
  const { t } = useTranslation();

  const { id = "", id1 = "", id2 = "" } = useParams();
  const [searchParams] = useSearchParams();
  const areParamsIdsValid = useAreParamsIdsValid();

  const openAttachment = useOpenAttachment();

  useEffect(() => {
    if (areParamsIdsValid) {
      const { download, forceSave } = ATTACHMENT_DOWNLOADS[type];

      openAttachment.mutate({ download: () => download({ id, id1, id2, searchParams }), forceSave });
    }
  }, [type, id, id1, id2, searchParams]);

  if (!areParamsIdsValid) {
    return <NotFound />;
  }

  if (openAttachment.data?.saved) {
    return (
      <Result
        status="success"
        styles={{ root: { marginTop: 100 } }}
        title={t("helpers.fileDownloaded")}
        subTitle={t("helpers.closeTab")}
      />
    );
  }

  if (openAttachment.isError) {
    return (
      <Result
        status="error"
        styles={{ root: { marginTop: 100 } }}
        title={t("helpers.fileDownloadFailed")}
        subTitle={t("helpers.closeTab")}
      />
    );
  }

  return (
    <Spin spinning={openAttachment.isIdle || openAttachment.isPending} size="large">
      <iframe
        id={ATTACHMENT_VIEWER_ID}
        title={ATTACHMENT_VIEWER_ID}
        style={{
          position: "absolute",
          border: 0,
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: "100%",
          height: "100svh",
        }}
        allowFullScreen
      />
    </Spin>
  );
};
